/**
 * Public user portfolio — /perfil/:userId
 * Displays only the certificates the user has explicitly opted-in to share publicly.
 * No email or role revealed. Auditors and recruiters can browse to verify credentials.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { BadgeCheck, GraduationCap, Loader2, ShieldCheck, ExternalLink, AlertTriangle } from 'lucide-react';
import useMetaTags from '../../hooks/useMetaTags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FW_UI = {
  nis2: { tone: 'bg-sky-50 text-sky-700 border-sky-200' },
  ai_act: { tone: 'bg-violet-50 text-violet-700 border-violet-200' },
  both: { tone: 'bg-slate-100 text-slate-700 border-slate-200' },
};

export default function PublicPortfolio() {
  const { userId } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useMetaTags({
    title: state.data ? `Portefólio · ${state.data.display_name} · NIS2AI` : 'Portefólio · NIS2AI',
    description: 'Certificados de formação NIS2 e AI Act partilhados publicamente.',
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/public/verify/portfolio/${userId}`);
        setState({ loading: false, data, error: null });
      } catch (err) {
        setState({ loading: false, data: null, error: err?.response?.data?.detail || 'Perfil não encontrado.' });
      }
    })();
  }, [userId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      <div className="max-w-4xl mx-auto px-6 py-16" data-testid="portfolio-page">
        <Link to="/" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-900 text-sm mb-8">
          <ShieldCheck className="h-4 w-4" /> NIS2AI · Portefólio Público
        </Link>

        {state.loading && (
          <div className="p-16 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-indigo-600" />
          </div>
        )}

        {state.error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center" data-testid="portfolio-error">
            <AlertTriangle className="h-12 w-12 mx-auto text-red-600 mb-3" />
            <h1 className="text-xl font-semibold text-red-900 mb-2">Perfil não encontrado</h1>
            <p className="text-sm text-red-700">{state.error}</p>
          </div>
        )}

        {state.data && (
          <div className="space-y-6" data-testid="portfolio-content">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center mb-3">
                <GraduationCap className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="portfolio-name">{state.data.display_name}</h1>
              <p className="text-sm text-slate-500 mt-1">
                {state.data.total > 0
                  ? `${state.data.total} certificado(s) partilhado(s) publicamente`
                  : 'Ainda sem certificados públicos'}
              </p>
            </div>

            {state.data.total === 0 ? (
              <div className="bg-slate-50 border border-slate-200 border-dashed rounded-xl p-12 text-center text-sm text-slate-500">
                Este utilizador ainda não partilhou nenhum certificado publicamente.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {state.data.items.map(c => {
                  const fw = FW_UI[c.framework] || FW_UI.both;
                  return (
                    <a
                      key={c.cert_id}
                      href={`/verify/certificate/${c.cert_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group bg-white rounded-xl border border-slate-200 hover:border-indigo-300 hover:shadow-md p-5 transition-all block"
                      data-testid={`portfolio-cert-${c.cert_id}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className={`text-[10px] font-semibold uppercase tracking-wide rounded px-2 py-0.5 border ${fw.tone}`}>{c.framework_label}</span>
                        <BadgeCheck className="h-5 w-5 text-emerald-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-indigo-700">{c.course_title}</h3>
                      <div className="flex items-center justify-between text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100">
                        <span>Nota <strong className="text-emerald-700 tabular-nums">{c.score}%</strong></span>
                        <span>{(c.issued_at || '').slice(0, 10)}</span>
                        <span className="inline-flex items-center gap-1 text-indigo-600 group-hover:underline">
                          Verificar <ExternalLink className="h-3 w-3" />
                        </span>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}

            <div className="text-center text-[11px] text-slate-400">
              Cada certificado é assinado criptograficamente com SHA-256 e verificável de forma independente.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
